import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SendMoneyData } from '../../types';
import axios from 'axios';

const baseURL = import.meta.env.VITE_API_URL;

interface MoneyState {
    loading: boolean;
    error: string | null;
    success: boolean;
    transactionId?: string;
    fetchingInmate: boolean;
    inmateName: string | null;
    inmateError: string | null;
}

const initialState: MoneyState = {
    loading: false,
    error: null,
    success: false,
    transactionId: undefined,
    fetchingInmate: false,
    inmateName: null,
    inmateError: null,
};

export const fetchInmateInfo = createAsyncThunk(
    'money/fetchInmateInfo',
    async (inmateCode: string, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseURL}/api/prison/search-inmate`, {
                inmate_code: inmateCode
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch inmate info');
        }
    }
);

export const sendMoney = createAsyncThunk(
    'money/sendMoney',
    async (data: SendMoneyData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseURL}/api/send-money`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send money');
        }
    }
);

const moneySlice = createSlice({
    name: 'money',
    initialState,
    reducers: {
        resetMoneyState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
            state.transactionId = undefined;
            state.fetchingInmate = false;
            state.inmateName = null;
            state.inmateError = null;
        },
        clearInmateInfo: (state) => {
            state.inmateName = null;
            state.inmateError = null;
            state.fetchingInmate = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Inmate Info
            .addCase(fetchInmateInfo.pending, (state) => {
                state.fetchingInmate = true;
                state.inmateError = null;
                state.inmateName = null;
            })
            .addCase(fetchInmateInfo.fulfilled, (state, action) => {
                state.fetchingInmate = false;
                state.inmateName = action.payload.data.inmate_names;
            })
            .addCase(fetchInmateInfo.rejected, (state, action) => {
                state.fetchingInmate = false;
                state.inmateError = action.payload as string;
                state.inmateName = null;
            })
            // Send Money
            .addCase(sendMoney.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(sendMoney.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.transactionId = action.payload.transactionId;
            })
            .addCase(sendMoney.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.success = false;
            });
    },
});

export const { resetMoneyState, clearInmateInfo } = moneySlice.actions;
export default moneySlice.reducer;